import * as React from "react";
import {useEffect, useState, useContext, useCallback} from "react";
import { ModelPartnerPartner, ModelPartner, ModelUser } from "@dia-ui/util_interlink-api";
import { AppContext } from "../../context/AppContext";
import IconComponent from "../IconComponent";
import PartnerConfigForm from "../PartnerConfigForm";

interface PartnerDisplayFormProps {
  partner: ModelPartnerPartner | undefined;
  type: string | undefined;
}

const PartnerDisplayForm: React.FC<PartnerDisplayFormProps> = ({ partner, type }) => {
  const {
    services: {
      serviceUser,
      serviceAuth
    },
    I18N,
    getRoutes
  } = useContext(AppContext);

  /**
   * ROUTE_CONFIG
   */
  const ROUTE_CONFIG = getRoutes();

  /**
   * state variables
   */
  const [partnerData, setPartnerData] = useState<ModelPartnerPartner | undefined>(partner);
  const [contactData, setContactData] = useState<ModelPartner | ModelUser | undefined>();
  const [contactType, setContactType] = useState<string>("partner");

  /**
   * get partner by type
   * @param partner
   * @param type
   */
  const getPartner = useCallback((partner: ModelPartnerPartner | undefined, type: string | undefined): ModelPartner => {
    return (type && partner && partner[type]) as ModelPartner || undefined;
  }, []);

  /**
   * get contact by type and contact id, if not set we return current customer or supplier as contact
   * @param partner
   * @param type
   */
  const getContact = useCallback(async (partner: ModelPartnerPartner, type: string | undefined): Promise<ModelPartner | ModelUser> => {
    // get direct contact by id from user if possible
    let contactId = partner[type + '_contact_id'];

    if (contactId != null) {
      setContactType("user");

      return await serviceUser.getById(contactId) as ModelUser;
    }

    return getPartner(partner, type);
  }, [getPartner, serviceUser]);

  /**
   * loadData
   * @param partner
   * @param type
   */
  const loadData = useCallback(async (partner: ModelPartnerPartner, type: string | undefined) => {
    setPartnerData(partner);
    setContactData(await getContact(partner, type));
  }, [getContact]);

  /**
   * setPartnerData
   */
  useEffect(() => {
    if (partner) {
      loadData(partner, type);
    }
  }, [loadData, partner ,type]);

  /**
   * renderContactUser
   */
  const renderContactUser = (_user: ModelUser | undefined) => {
    if (!_user?.fon && !_user?.email) {
      return false;
    }

    return (
      _user ? <>
        { _user.title } { _user.firstname } { _user.lastname }
        <br />
        { _user.fon && (
          <>
            { _user.fon }
            <br />
          </>
        ) }
        { _user.email && (
          <>
            { _user.email }
            <br />
          </>
        ) }
        { _user.linkedin && (
          <>
            { _user.linkedin }
            <br />
          </>
        ) }
      </> : ''
    )
  };

  /**
   * renderContactPartner
   */
  const renderContactPartner = (_partner: ModelPartner |  undefined) => {
    if (!_partner?.fon && !_partner?.email) {
      return false;
    }

    return (
      _partner ? <>
        { _partner.name && (
          <>
            { _partner.name }
            <br />
          </>
        ) }
        <br />
        { _partner.fon && (
          <>
            { _partner.fon }
            <br />
          </>
        ) }
        { _partner.email && (
          <>
            { _partner.email }
            <br />
          </>
        ) }
        { _partner.linkedIn && (
          <>
            { _partner.linkedIn }
            <br />
          </>
        ) }
      </> : ''
    )
  };

  /**
   * renderPartner
   */
  const renderPartner = (_partner: ModelPartner |  undefined) => {
    return (
      _partner ? <>
        { _partner.name && (
          <>
            { _partner.name }
            <br />
          </>
        ) }
        { _partner.name2 && (
          <>
            { _partner.name2 }
            <br />
          </>
        ) }
        { _partner.street } { _partner.houseNumber }<br />
        { _partner.country } { _partner.zip } { _partner.city }<br />
        <br />
        { _partner.fon && (
          <>
            { _partner.fon }
            <br />
          </>
        ) }
        { _partner.email && (
          <>
            { _partner.email }
            <br />
          </>
        ) }
        { _partner.linkedIn && (
          <>
            { _partner.linkedIn }
            <br />
          </>
        ) }
      </> : ''
    )
  };

  //@ts-ignore
  const contact = contactType === "user" ? renderContactUser(contactData) : renderContactPartner(contactData),
    /**
     * only the supplier of the relation may edit the config; the backend
     * enforces the same rule with a 403 on PUT /partnerpartner/{id}
     */
    authUser = serviceAuth.getUser(),
    configEditable = !!authUser?.partner_id && !!partnerData && authUser.partner_id === partnerData.supplierId;

  return (
    <dia-grid-nested data-grid-nested-full-width>
      <dia-grid-row data-grid-column-stretch>
        <dia-grid-column>
          <dia-panel data-fullwidth>
            <dia-panel-header>
              <IconComponent iconName={ ROUTE_CONFIG.PARTNER_OVERVIEW.icon } />
              { I18N.t("Partner Daten") }
            </dia-panel-header>
            <dia-panel-content>
              <dia-grid-nested>
                <dia-grid-row>
                  <dia-grid-column>
                    <p>
                      { renderPartner(getPartner(partner, type)) }
                    </p>
                  </dia-grid-column>
                </dia-grid-row>
              </dia-grid-nested>
            </dia-panel-content>
          </dia-panel>
        </dia-grid-column>
        { contact ?
          <dia-grid-column>
            <dia-panel data-fullwidth>
              <dia-panel-header>
                <IconComponent iconName="user" />
                { I18N.t("Ansprechpartner") }
              </dia-panel-header>
              <dia-panel-content>
                <dia-grid-nested>
                  <dia-grid-row>
                    <dia-grid-column>
                      <p>
                        { contact }
                      </p>
                    </dia-grid-column>
                  </dia-grid-row>
                </dia-grid-nested>
              </dia-panel-content>
            </dia-panel>
          </dia-grid-column> : ''
        }
      </dia-grid-row>
      { partnerData && (
        <PartnerConfigForm
          partner={ partnerData }
          editable={ configEditable }
          onSaved={ (updated) => setPartnerData(updated) }
        />
      ) }
    </dia-grid-nested>
  );
};

export default PartnerDisplayForm;
